import FileRendererBlob from '@github-ui/file-renderer-blob'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {useQuery, type QueryKey} from '@github-ui/react-query'
import type {RichDiff as RichDiffType} from '../types'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {Spinner} from '@primer/react'
import styles from './RichDiff.module.css'

type RichDiffFetcher<TArgs> = (args: TArgs) => Promise<RichDiffResponse | undefined>

type RichDiffResponse = Pick<RichDiffType, 'proseDiffHtml' | 'renderInfo' | 'dependencyDiffPath'>

interface QueryConfig<TArgs = unknown> {
  fetcher: RichDiffFetcher<TArgs>
  key: QueryKey
  args: TArgs
}

export interface RichDiffProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryConfig?: QueryConfig<any> // Encapsulated query configuration - until we can move diff-lines into pull-requests
  initialData?: RichDiffResponse // Optional initial data for the rich diff
}

export function RichDiff({queryConfig, initialData}: RichDiffProps) {
  let normalizedInitialData = undefined

  // if initialData is provided, normalize it to match the expected structure
  // otherwise set to undefined so it's not cached in the query (otherwise a fetch will not be made)
  if (initialData && (initialData.proseDiffHtml || initialData.renderInfo || initialData.dependencyDiffPath)) {
    normalizedInitialData = {
      proseDiffHtml: initialData.proseDiffHtml,
      renderInfo: initialData.renderInfo,
      dependencyDiffPath: initialData.dependencyDiffPath,
    }
  }

  const queryResult = useQuery({
    queryKey: [queryConfig?.key, queryConfig?.args],
    queryFn: async () => {
      return (await queryConfig?.fetcher(queryConfig.args)) || {}
    },
    initialData: normalizedInitialData,
    staleTime: Infinity,
    enabled: !!queryConfig,
  })

  const {data, isLoading} = queryConfig ? queryResult : {data: normalizedInitialData, isLoading: false}

  const {proseDiffHtml, renderInfo, dependencyDiffPath} = data || {}

  if (isLoading) {
    return (
      <div className="d-flex flex-justify-center p-4">
        <Spinner size="large" />
      </div>
    )
  }

  if (proseDiffHtml) {
    return (
      <div className="prose-diff position-relative">
        <VerifiedHTMLBox html={proseDiffHtml} />
      </div>
    )
  } else if (renderInfo) {
    return (
      <FileRendererBlob
        {...renderInfo}
        viewportClassName={styles.fileRendererViewport}
        iframeClassName={styles.fileRendererIframe}
      />
    )
  } else if (dependencyDiffPath) {
    return (
      <div>
        <IncludeFragment src={dependencyDiffPath}>
          <div className="text-center py-3">
            <Spinner size="medium" />
            <p className="color-fg-muted my-2">Loading Dependency Review...</p>
          </div>
        </IncludeFragment>
      </div>
    )
  } else {
    return <div className="d-flex flex-justify-center p-4">This rich diff is not supported at the moment.</div>
  }
}

try{ RichDiff.displayName ||= 'RichDiff' } catch {}